import * as React from "react";
import { ADLoginForm } from "./ADLoginForm";
import { LocalLoginForm } from "./LocalLoginForm";
import { StudentOrGuardianDialog } from "./StudentOrGuardianDialog";
import { TLCEvents } from "../events/events";
import { LS2PAC_CONSTANTS } from "../../main/constants";
import { useSupportedFunctionality } from "./SupportedFunctionality";
import { useLocationConfiguration } from "./LocationConfiguration";
import { useGlobalConfig } from "./GlobalConfig";
import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { TLCEventManager } from "../events/EventManager";
import { removeCookie, setCookie } from "typescript-cookie";
import { ForgotPasswordDialogProvider } from "./ForgotPasswordDialog";
import { useStationLocation } from "./StationLocation";
import { useWindowVariables } from "./WindowVariables";
import { useLoginContext } from "./LoginContext";
import { useAppLocation } from "./LocationContext";

export interface LoginDialogProps {
  isKids: boolean;
}

export interface LoginCredentials {
  username: string;
  password: string;
  rememberMe: boolean;
  localLoginOnly: boolean;
}

export const LoginDialog: React.FC<LoginDialogProps> = (props) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const theme = useTheme();
  const { loginMessage } = useWindowVariables();
  const { stationLocation } = useStationLocation();

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { supportedFunctionality } = useSupportedFunctionality();
  const location = useAppLocation();
  const previousPathRef = React.useRef("");

  const {
    accountNotLinked: isAccountNotLinked,
    googleEmail: emailAddress,
    setAccountNotLinked,
    setGoogleEmail,
  } = useLoginContext();

  const { locationConfiguration: { pacSettings } = { pacSettings: null } } =
    useLocationConfiguration();

  const { globalSettings, googleOauthEnabled, ldapEnabled, adfsSettings } =
    useGlobalConfig();

  const displayGuardianLogin = React.useMemo(() => {
    if (!pacSettings || !supportedFunctionality) {
      return false;
    }
    const { useGuardianProhibition, includeGuardiansInAD } =
      supportedFunctionality;
    const { displaySettings } = pacSettings;
    return (
      useGuardianProhibition === true &&
      includeGuardiansInAD === false &&
      displaySettings.displayGuardianLogin === true
    );
  }, [pacSettings, supportedFunctionality]);

  const [loginForm, setLoginForm] = React.useState<"AD" | "LOCAL" | "SSO">(
    null
  );

  React.useEffect(() => {
    if (adfsSettings) {
      if (
        !adfsSettings.enabled &&
        isAccountNotLinked === true &&
        emailAddress
      ) {
        setOpen(true);
      }
    }
  }, [adfsSettings, isAccountNotLinked, emailAddress]);

  const [showLoginError, setShowLoginError] = React.useState(false);
  const [defaultPassword, setDefaultPassword] = React.useState("");
  const [defaultUsername, setDefaultUsername] = React.useState("");

  // TODO should no longer be needed
  const handleLegacyPath = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.hash.slice(1));
    // TODO can url.search contain accountNotLinked?

    // TODO this URL is not in use anymore (although we map to its current equivalent)
    if (params.get("section") === "obrsignup") {
      params.set("section", "home");
    }

    // TODO is this URL still in use?
    if (params.get("section") === "resetPIN") {
      params.set("section", "home");
    }

    if (params.has("accountNotLinked")) {
      params.delete("accountNotLinked");
    }

    // TODO this URL is not in use anymore (although we map to its current equivalent)
    if (params.size === 0) {
      params.set("section", "home");
    }
    url.hash = params.toString();
    return url.toString();
  };

  // TODO shift to ls2pac login context
  const handleReactRouterPath = () => {
    // This is reachable via prompt for login from actions like place hold
    const searchParam = window.location.search; // preserve ?config=x
    const searchParams = new URLSearchParams(location.search || "");
    searchParams.delete("accountNotLinked");
    const search = searchParams.toString();
    const hash = location.hash || "";
    const pathname = location.pathname || "/";
    return `/${searchParam}#${pathname}?${search || ""}${hash}`;
  };

  const replaceURLParameters = () => {
    // TODO move Kids specifics to a KidsLoginContext
    if (!location || location?.pathname === "/kids") {
      // parse kids URLs the old way as the hash doesn't follow a params pattern like LS2Pac
      const urlString = window.location.href.toString();
      var indexOfAccountNotLinked = urlString.indexOf("&accountNotLinked=true");
      if (indexOfAccountNotLinked !== -1) {
        return urlString.substring(0, indexOfAccountNotLinked);
      }
      return urlString;
    }

    // React router sticks everything in pathname for the legacy routes
    if (location && location.pathname.indexOf("section=") !== -1) {
      console.log("Legacy Path Found"); // This should be unreachable now
      return handleLegacyPath();
    }

    return handleReactRouterPath();
  };

  const getADFSRedirect = React.useCallback(
    (pathname: string, search: string, hash: string) => {
      if (!adfsSettings) return;
      const normalizedHash = hash
        .substring(1)
        .replace(new RegExp("%22", "g"), '"');
      return (
        adfsSettings.loginUrl +
        "?path=" +
        encodeURIComponent(pathname) +
        "&search=" +
        encodeURIComponent(search) +
        "&hash=" +
        encodeURIComponent(normalizedHash)
      );
    },
    [adfsSettings?.loginUrl]
  );

  const adfsRedirect = React.useRef<string>();

  // TODO shift to login contexts
  React.useEffect(() => {
    const handlePromptForLogin = (to: string | null, from: string | null) => {
      if (to) {
        // we know this is a react router path from LS2Pac
        const hashStart = to.indexOf("#");
        adfsRedirect.current = getADFSRedirect(
          "/",
          window.location.search, // perserve any search params (for example ?config=)
          to.substring(hashStart) // strip /# from hash
        );

        setCookie("SAVED_DESTINATION", to);
      } else {
        adfsRedirect.current = getADFSRedirect(
          window.location.pathname,
          window.location.search,
          window.location.hash
        );
        setCookie("SAVED_DESTINATION", replaceURLParameters());
      }

      if (from) {
        setCookie("SAVED_ORIGIN", from);
        console.log("SAVED_ORIGIN");
      }

      if (adfsSettings?.enabled && !displayGuardianLogin) {
        window.location.href = adfsRedirect.current;
        return;
      }
      setOpen(true);
    };
    // TODO type interface
    const handleLoginResponse = (event: any) => {
      if (event.success) {
        onClose();
        fireOnCloseLogin(true);
      } else {
        setShowLoginError(true);
      }
    };

    const handleTimeout = (event: any) => {
      onClose();
    };

    // TODO refactor dependence on event manager and LoginClientManager
    const promptEventGuid = TLCEvents.on(
      LS2PAC_CONSTANTS.Events.onPromptForLogin,
      (event: { to?: string; from?: string }) => {
        handlePromptForLogin(event.to, event.from);
      }
    );
    const responseEventGuid = TLCEvents.on(
      LS2PAC_CONSTANTS.Events.onLoginResponse,
      handleLoginResponse
    );

    const onTimeoutGuid = TLCEvents.on(
      LS2PAC_CONSTANTS.Events.onTimeout,
      handleTimeout
    );

    return () => {
      TLCEvents.off(LS2PAC_CONSTANTS.Events.onPromptForLogin, promptEventGuid);
      TLCEvents.off(LS2PAC_CONSTANTS.Events.onLoginResponse, responseEventGuid);
      TLCEvents.off(LS2PAC_CONSTANTS.Events.onTimeout, onTimeoutGuid);
    };
  }, [adfsSettings, displayGuardianLogin, location, getADFSRedirect]);

  React.useEffect(() => {
    if (open && adfsSettings) {
      // there is no sign in with google in an adfs setup
      if (adfsSettings.enabled) {
        setLoginForm("SSO");
        return;
      }

      if (isAccountNotLinked) {
        setLoginForm("LOCAL");
        return;
      }

      if (ldapEnabled) {
        setLoginForm("AD");
        return;
      }

      setLoginForm("LOCAL");
    }

    return () => {};
  }, [ldapEnabled, adfsSettings, isAccountNotLinked, open]);

  // TODO move this to a context, handle differently for LS2 Pac (react router) and LS2 Kids custom hash router
  // TODO is this needed anymore?
  // if we use handle: { secured: true }; on the LS2PacRouteObject it should handle this automatically
  //check for URL changes
  React.useEffect(() => {
    if (location.pathname === "/login") return; // don't replace previous path when navigating to /login

    const fullPath = `${location.pathname}${location.search}${location.hash}`;

    if (previousPathRef.current && previousPathRef.current !== fullPath) {
      onClose();
    }

    previousPathRef.current = fullPath;
  }, [location]);

  // TODO refactor out need for events and LoginClientManager
  const handleLogin = (creds: LoginCredentials) => {
    TLCEventManager.fireEvent(LS2PAC_CONSTANTS.Events.onLogin, {
      credentials: creds,
    });
  };

  const handleClose = (event: React.SyntheticEvent, reason?: string) => {
    if (reason === "backdropClick") {
      event.preventDefault();
      return;
    }
    onClose();
    fireOnCloseLogin();
  };

  const onClose = () => {
    setOpen(false);
    setShowLoginError(false);
    setDefaultPassword("");
    setDefaultUsername("");
    removeCookie("SAVED_DESTINATION");
    removeCookie("SAVED_ORIGIN");
    // reset Google Sign In data
    setGoogleEmail(null);
    setAccountNotLinked(null);
  };

  // TODO refactor dependency on event manager and login client manager, shift to contexts
  const fireOnCloseLogin = (closedViaSubmit: boolean = false) => {
    //TODO: Replace with reliable login context
    TLCEventManager.fireEvent(LS2PAC_CONSTANTS.Events.onCloseLogin, {
      closedViaSubmit,
      prevPath: previousPathRef.current,
    });
  };

  const showRememberMe =
    pacSettings?.displaySettings.showRememberMe && !stationLocation;

  return (
    <React.Fragment>
      {/* use open to conditionally render and reset initial state */}
      {open && loginForm !== "SSO" && (
        <Dialog open={open} onClose={handleClose} fullScreen={fullScreen}>
          {loginForm === "AD" && (
            <ADLoginForm
              showLoginError={showLoginError}
              loginMessage={loginMessage}
              onSubmitLogin={handleLogin}
              onClickClose={handleClose}
              defaultPassword={defaultPassword}
              defaultUsername={defaultUsername}
              displayGuardianLogin={displayGuardianLogin}
              {...props}
              onClickGuardianLogin={({ password, barcode }) => {
                setDefaultPassword(password);
                setDefaultUsername(barcode);
                setShowLoginError(false);
                setLoginForm("LOCAL");
              }}
            ></ADLoginForm>
          )}
          {globalSettings && pacSettings && loginForm === "LOCAL" && (
            <ForgotPasswordDialogProvider>
              <LocalLoginForm
                displayGuardianLogin={
                  (adfsSettings?.enabled || ldapEnabled) && displayGuardianLogin
                }
                showLoginError={showLoginError}
                loginMessage={loginMessage}
                onSubmitLogin={handleLogin}
                onClickClose={handleClose}
                defaultPassword={defaultPassword}
                defaultUsername={defaultUsername}
                onClickBack={({ barcode, password }) => {
                  setLoginForm(ldapEnabled ? "AD" : "SSO");
                  setShowLoginError(false);
                  if (ldapEnabled) {
                    setDefaultPassword(password);
                    setDefaultUsername(barcode);
                  }
                }}
                isGoogleLoginEnabled={
                  pacSettings.googleSSOEnabled &&
                  googleOauthEnabled &&
                  !adfsSettings?.enabled
                }
                accountNotLinked={isAccountNotLinked ? emailAddress : null}
                showRememberMe={showRememberMe}
                useForgotPasswordLink={globalSettings.useForgotPasswordLink}
                showResetPINLink={pacSettings.displaySettings.showResetPINLink}
                {...props}
              ></LocalLoginForm>
            </ForgotPasswordDialogProvider>
          )}
        </Dialog>
      )}
      {open && loginForm === "SSO" && displayGuardianLogin && (
        <StudentOrGuardianDialog
          onClickGuardianLogin={() => setLoginForm("LOCAL")}
          onClickStudentLogin={() => {
            window.location.href = adfsRedirect.current;
          }}
        ></StudentOrGuardianDialog>
      )}
    </React.Fragment>
  );
};
